import { useState } from "react";
import { formatTime, calcProduction, buildingData, calcBuildingBuildTime, calcResearchBuildTime, calcResourceGatherTime, researchData, shipData, defenseData, calcShipBuildTime, actionTypes } from "../data";
import PlanetCategoryTabs from "./PlanetCategoryTabs";
import DraggablePlanetAction from "./DraggablePlanetAction";

function PlanetDisplay({ state, handleUpgrade, handleTech, handleShip, handleDefense, addSetProduction, addTransport, handleAddPlanet, handleDeletePlanet, setDraggedItem, updatePreviewState, activePlanetId, onOpenAutoPlan }) {
    // Get only the active planet
    const activePlanet = activePlanetId && state.planets[activePlanetId] 
      ? { ...state.planets[activePlanetId], id: activePlanetId, production: calcProduction(state, activePlanetId) }
      : null;
      
    // Fallback to showing all planets if no active planet is selected
    const planets = activePlanet 
      ? [activePlanet] 
      : Object.keys(state.planets).map((key, index) => {
          return {... state.planets[key], id: key, production: calcProduction(state, key)}
        });

    const [activeCategory, setActiveCategory] = useState('buildings');

    const [shipBuildCounts, setShipBuildCounts] = useState(() => {
      let ret = {}
      Object.keys(shipData).forEach((ship) => {
        ret[ship] = 1;
      })
      return ret;
    });

    const handleChange = (e) => {
      const { name, value, } = e.target;
      setShipBuildCounts((prev) => {
          prev[name] = parseInt(value);
          return { ... prev };
      });
  }

    const [defenseBuildCounts, setDefenseBuildCounts] = useState(() => {
      let ret = {}
      Object.keys(defenseData).forEach((defense) => {
        ret[defense] = 1;
      })
      return ret;
    });

    const handleDefenseChange = (e) => {
      const { name, value, } = e.target;
      setDefenseBuildCounts((prev) => {
          prev[name] = parseInt(value);
          return { ... prev };
      });
  }

    return (
      <section className="w-full h-full overflow-y-auto bg-gray-800 p-4">
        {planets.map((planet) => ( 
          <div key={planet.id} className="w-full bg-gray-600 text-white rounded-lg shadow-lg">
            {/* Header with Planet Name */}
            <header className="text-2xl font-bold p-3 bg-gray-700 rounded-t-lg flex justify-between items-center">
              <div className="flex items-center">
                <span className="mr-2">{planet.name}</span>
                <span className="text-sm text-gray-300">[{planet.id}:1:1]</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onOpenAutoPlan(planet.id)}
                  className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                >
                  Auto Plan
                </button>
                {planet.id != "1" && (
                  <button
                  onClick={() => handleDeletePlanet(planet.id)}
                  className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs"
                      >
                        Delete Planet
                  </button>
                )}
              </div>
            </header>
            <div className="p-3">

            {/* Resources and Production */}
            <div className="mb-6 bg-gray bg-gray-700">
              <div className="flex justify-between">
                <div className="text-gray-100 text-left">
                  <h3 className="text-lg font-semibold text-gray-100">Resources</h3>
                  <p>Metal: {Math.round(planet.resources.metal)}</p>
                  <p>Crystal: {Math.round(planet.resources.crystal)}</p>
                  <p>Deuterium: {Math.round(planet.resources.deuterium)}</p>
                </div>
                <div className="text-right">
                  <h3 className="text-lg font-semibold">Production</h3>
                  <p>Metal: {Math.round(planet.production.metal)}/h</p>
                  <p>Crystal: {Math.round(planet.production.crystal)}/h</p>
                  <p>Deuterium: {Math.round(planet.production.deuterium)}/h</p>
                  <p>Energy: {Math.round(planet.production.energy)}/h</p>
                </div>
              </div>
              <div className="flex justify-between">
                  <button
                    onClick={ () => {addTransport(planet.id)}}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
                  >
                  {/* <FaArrowRight className="text-lg" /> */}
                  {"Transport"}
                </button>
                {/* Apply Button */}
                <button
                  onClick={ () => {addSetProduction(planet.id)}}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
                >
                  {/* <FaArrowRight className="text-lg" /> */}
                  {"Set Factors"}
                </button>
              </div>
            </div>

            <PlanetCategoryTabs active={activeCategory} onChange={setActiveCategory} />

            {activeCategory === 'buildings' && (
              /* Buildings Section */
              <div className = "bg-gray-700">

                {Object.keys(planet.buildings).map((building) => {
                  const level = planet.buildings[building];
                  const cost = buildingData[building].cost(level + 1);
                  const timeToGather = calcResourceGatherTime(state, planet.id, {...cost});
                  const buildTime = calcBuildingBuildTime(state, planet.id, {... cost});

                  return (
                    <div
                      key={building}
                      className="grid grid-cols-6 items-center gap-x-2 py-2 pl-3 pr-3 text-sm text-gray-200 border-b border-gray-600"
                    >
                      {/* Building name */}
                      <div className="text-left col-span-2 font-semibold">{buildingData[building].name}</div>

                      {/* Level */}
                      <div className="col-span-1 text-left">{level}</div>
                      
                      {/* Cost */}
                      <div className="col-span-2 text-xs text-left">
                        M: {cost.metal}, C: {cost.crystal}, D: {cost.deuterium}
                      </div>

                      {/* Upgrade Button */}
                      <div className="col-span-1 text-right">
                        <DraggablePlanetAction 
                          actionType={actionTypes.UPGRADE_BUILDING}
                          payload={{ planetId: planet.id, building }}
                          setDraggedItem={setDraggedItem}
                          updatePreviewState={updatePreviewState}
                        >
                          <button
                            onClick={() => handleUpgrade(planet.id, building)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
                          >
                            +1
                          </button>
                        </DraggablePlanetAction>
                      </div>

                      {/* Time to build & Time to gather (optional, could be shown on hover or in tooltip) */}
                      <div className="col-span-5 text-xs text-gray-400 mt-1 text-left">
                        Build: {formatTime(buildTime)} | Gather: {formatTime(timeToGather)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {activeCategory === 'fleet' && (
              <div className = "bg-gray-700">
                {Object.keys(planet.ships).map((ship) => {
                  const count = planet.ships[ship];
                  const cost = shipData[ship].cost(0);
                  const timeToGather = calcResourceGatherTime(state, planet.id, {...cost});
                  const buildTime = calcShipBuildTime(state, planet.id, {... cost});

                  return (
                    <div
                      key={ship}
                      className="grid grid-cols-6 items-center gap-x-2 py-2 pl-3 pr-3 text-sm text-gray-200 border-b border-gray-600"
                    >
                      {/* Building name */}
                      <div className="text-left col-span-2 font-semibold">{shipData[ship].name}</div>

                      {/* Level */}
                      <div className="col-span-1 text-left">{count}</div>
                      
                      {/* Cost */}
                      <div className="col-span-2 text-xs text-left">
                        M: {cost.metal}, C: {cost.crystal}, D: {cost.deuterium}
                      </div>

                      {/* Time to build & Time to gather (optional, could be shown on hover or in tooltip) */}
                      <div className="col-span-2 text-xs text-gray-400 mt-1 text-left">
                        Build: {formatTime(buildTime)} <br/> Gather: {formatTime(timeToGather)}
                      </div>

                      {/* Upgrade Button */}
                      <div className="col-span-4 text-right">
                        <input type="number" name={ship} min="0" value={shipBuildCounts[ship]} onChange={handleChange} step="1" className="bg-gray-700 text-white p-1 rounded-md w-24" />
                        <DraggablePlanetAction 
                          actionType={actionTypes.BUILD_SHIP}
                          payload={{ planetId: planet.id, ship, count: shipBuildCounts[ship] }}
                          setDraggedItem={setDraggedItem}
                          updatePreviewState={updatePreviewState}
                        >
                          <button
                            onClick={() => handleShip(planet.id, ship, shipBuildCounts[ship])}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
                          >
                            +
                          </button>
                        </DraggablePlanetAction>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {activeCategory === 'defense' && (
              <div className = "bg-gray-700">
                {Object.keys(planet.defenses).map((defense) => {
                  const count = planet.defenses[defense];
                  const cost = defenseData[defense].cost(0);
                  const timeToGather = calcResourceGatherTime(state, planet.id, {...cost});
                  const buildTime = calcShipBuildTime(state, planet.id, {... cost});

                  return (
                    <div
                      key={defense}
                      className="grid grid-cols-6 items-center gap-x-2 py-2 pl-3 pr-3 text-sm text-gray-200 border-b border-gray-600"
                    >
                      {/* Defense name */}
                      <div className="text-left col-span-2 font-semibold">{defenseData[defense].name}</div>

                      {/* Count */}
                      <div className="col-span-1 text-left">{count}</div>

                      {/* Cost */}
                      <div className="col-span-2 text-xs text-left">
                        M: {cost.metal}, C: {cost.crystal}, D: {cost.deuterium}
                      </div>

                      {/* Time to build & Time to gather (optional, could be shown on hover or in tooltip) */}
                      <div className="col-span-2 text-xs text-gray-400 mt-1 text-left">
                        Build: {formatTime(buildTime)} <br/> Gather: {formatTime(timeToGather)}
                      </div>

                      {/* Build Button */}
                      <div className="col-span-4 text-right">
                        <input type="number" name={defense} min="0" value={defenseBuildCounts[defense]} onChange={handleDefenseChange} step="1" className="bg-gray-700 text-white p-1 rounded-md w-24" />
                        <DraggablePlanetAction
                          actionType={actionTypes.BUILD_DEFENSE}
                          payload={{ planetId: planet.id, defense, count: defenseBuildCounts[defense] }}
                          setDraggedItem={setDraggedItem}
                          updatePreviewState={updatePreviewState}
                        >
                          <button
                            onClick={() => handleDefense(planet.id, defense, defenseBuildCounts[defense])}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
                          >
                            +
                          </button>
                        </DraggablePlanetAction>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {activeCategory === 'research' && (
              <div className = "bg-gray-700">

                {Object.keys(state.research).map((tech) => {
                  const level = state.research[tech];
                  const cost = researchData[tech].cost(level + 1);
                  const timeToGather = calcResourceGatherTime(state, planet.id, {...cost});
                  const buildTime = calcResearchBuildTime(state, planet.id, {... cost}, researchData[tech].available);

                  return (
                    <div
                      key={tech}
                      className="grid grid-cols-6 items-center gap-x-2 py-2 pl-3 pr-3 text-sm text-gray-200 border-b border-gray-600"
                    >
                      {/* Building name */}
                      <div className="text-left col-span-2 font-semibold">{researchData[tech].name}</div>

                      {/* Level */}
                      <div className="col-span-1 text-left">{level}</div>
                      
                      {/* Cost */}
                      <div className="col-span-2 text-xs text-left">
                        M: {cost.metal}, C: {cost.crystal}, D: {cost.deuterium}
                      </div>

                      {/* Upgrade Button */}
                      <div className="col-span-1 text-right">
                        <DraggablePlanetAction 
                          actionType={actionTypes.RESEARCH}
                          payload={{ planetId: planet.id, tech }}
                          setDraggedItem={setDraggedItem}
                          updatePreviewState={updatePreviewState}
                        >
                          <button
                            onClick={() => handleTech(planet.id, tech)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
                          >
                            +1
                          </button>
                        </DraggablePlanetAction>
                      </div>

                      {/* Time to build & Time to gather (optional, could be shown on hover or in tooltip) */}
                      <div className="col-span-5 text-xs text-gray-400 mt-1 text-left">
                        Build: {formatTime(buildTime)} | Gather: {formatTime(timeToGather)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </div>
          </div>
        ))}
      </section>
    );
  }
  
  export default PlanetDisplay;